import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { BudgetPeriod } from '@/api/types';
import { useCreatePeriod, useDeletePeriod, useUpdatePeriod } from '@/api/hooks';
import { centsToInput, parseMoneyToCents } from '@/lib/format';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Field, TextArea, TextInput } from './ui/Field';
import { ConfirmDialog } from './ui/ConfirmDialog';

interface PeriodDialogProps {
  open: boolean;
  /** null → create mode. */
  period: BudgetPeriod | null;
  /** Year to pre-fill in create mode. */
  suggestedYear: number;
  onClose: () => void;
}

export function PeriodDialog({ open, period, suggestedYear, onClose }: PeriodDialogProps) {
  const isEdit = period !== null;
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const createPeriod = useCreatePeriod();
  const updatePeriod = useUpdatePeriod();
  const deletePeriod = useDeletePeriod();

  useEffect(() => {
    if (!open) return;
    setName(period?.name ?? String(suggestedYear));
    setStartDate(period?.startDate ?? `${suggestedYear}-01-01`);
    setEndDate(period?.endDate ?? `${suggestedYear}-12-31`);
    setAmount(period ? centsToInput(period.amount) : '');
    setNote(period?.note ?? '');
    setError(null);
  }, [open, period, suggestedYear]);

  /** Keeps name and end in step while the start is still a plain January 1st. */
  const onStartChange = (value: string) => {
    setStartDate(value);
    const year = value.slice(0, 4);
    if (value.endsWith('-01-01') && /^\d{4}$/.test(year)) {
      setEndDate(`${year}-12-31`);
      if (/^\d{4}$/.test(name) || name === '') setName(year);
    }
  };

  const save = () => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Bitte einen Namen angeben.');
      return;
    }
    if (startDate === '' || endDate === '') {
      setError('Bitte Anfang und Ende angeben.');
      return;
    }
    if (endDate < startDate) {
      setError('Das Ende darf nicht vor dem Anfang liegen.');
      return;
    }
    const cents = parseMoneyToCents(amount);
    if (cents === null) {
      setError('Bitte ein Budget angeben.');
      return;
    }

    const payload = {
      name: trimmed,
      startDate,
      endDate,
      amount: cents,
      note: note.trim() === '' ? null : note.trim(),
    };
    const onError = (mutationError: Error) => setError(mutationError.message);
    if (isEdit) {
      updatePeriod.mutate({ id: period.id, ...payload }, { onSuccess: onClose, onError });
    } else {
      createPeriod.mutate(payload, { onSuccess: onClose, onError });
    }
  };

  const pending = createPeriod.isPending || updatePeriod.isPending;

  return (
    <>
      <Modal
        open={open}
        title={isEdit ? 'Budgetperiode bearbeiten' : 'Neue Budgetperiode'}
        description="Standard ist ein Kalenderjahr. Perioden dürfen sich nicht überlappen."
        onClose={onClose}
        footer={
          <>
            {isEdit ? (
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                className="mr-auto hover:text-[var(--critical)]"
              >
                <Trash2 size={15} />
                Löschen
              </Button>
            ) : null}
            <Button onClick={onClose}>Abbrechen</Button>
            <Button variant="primary" onClick={save} disabled={pending}>
              {pending ? 'Speichern …' : 'Speichern'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Name" error={error}>
            {(id) => (
              <TextInput
                id={id}
                value={name}
                autoFocus
                placeholder="z. B. 2027"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Von">
              {(id) => (
                <TextInput
                  id={id}
                  type="date"
                  value={startDate}
                  onChange={(event) => onStartChange(event.target.value)}
                />
              )}
            </Field>
            <Field label="Bis">
              {(id) => (
                <TextInput
                  id={id}
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              )}
            </Field>
          </div>

          <Field label="Budget (€)" hint="Was in diesem Zeitraum insgesamt ausgegeben werden darf.">
            {(id) => (
              <TextInput
                id={id}
                inputMode="decimal"
                className="num"
                placeholder="z. B. 60000"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>

          <Field label="Notiz">
            {(id) => (
              <TextArea
                id={id}
                value={note}
                placeholder="Woher kommt das Geld, was ist eingeplant …"
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Budgetperiode löschen?"
        message="Nur das Budget wird entfernt. Die Investitionen in diesem Zeitraum bleiben unverändert."
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          if (!period) return;
          deletePeriod.mutate(period.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              onClose();
            },
          });
        }}
      />
    </>
  );
}
